import { Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  GridDataResult,
  DataStateChangeEvent,
  FilterService,
} from '@progress/kendo-angular-grid';
import { GroupDescriptor, SortDescriptor, CompositeFilterDescriptor } from '@progress/kendo-data-query';
import {
  ColumnOptions,
  GridState,
  MessageService,
  LoaderService,
  ValidationResultService,
} from '@shared';
import { GridRowModifierException } from '../../../models';
import { ReportService } from '../../../services';
import { Tier, TiersService } from '@shared-data';

@Component({
  selector: 'app-modifier-exceptions',
  templateUrl: './modifier-exceptions.component.html',
  styleUrl: './modifier-exceptions.component.scss',
})
export class ModifierExceptionComponent {
  modifierExceptions: GridDataResult = { data: [], total: 0 };

  columns: ColumnOptions[] = [];

  state!: GridState;

  pageSize: number = 10;

  fontSize: number = 0.875;

  initialSort: SortDescriptor[] = [
    { field: 'tierName', dir: 'asc' },
    { field: 'sortOrder', dir: 'asc' },
  ];

  group: GroupDescriptor[] = [{ field: 'panelName' }];

  reportType!: string;

  tiers: Tier[] = [];

  selectedTier: Tier = new Tier({ id: '-1', name: 'None' });

  get customFilterView(): string {
    return 'modifiers/' + this.reportType;
  }

  filterCache: Map<string, string[]> = new Map<string, string[]>();

  onCustomFilterChange(
    column: string,
    event: string[],
    filterService: FilterService,
  ) {
    this.filterCache.set(column, event);

    filterService.filter({
      filters: event.map((value) => ({
        field: column,
        operator: 'eq',
        value,
      })),
      logic: 'or',
    });
  }

  filterChanged(event: CompositeFilterDescriptor) {
    if (event.filters.length === 0) {
      this.filterCache.clear();
    }

    for (const field of this.filterCache.keys()) {
      if (!GridState.hasField(event, field)) {
        this.filterCache.delete(field);
      }
    }
  }

  constructor(
    private reportService: ReportService,
    private messageService: MessageService,
    private loaderService: LoaderService,
    private tierService: TiersService,
    private route: ActivatedRoute,
    private validationErrorService: ValidationResultService,
  ) {
    this.columns = new GridRowModifierException().getColumns();
  }

  getTiers() {
    this.loaderService.show();

    this.tierService.getTiers().subscribe({
      next: (tiers) => {
        this.tiers = tiers;
        this.selectedTier = this.tiers[0];
        this.setDefaultState();
        this.loaderService.hide();
      },
      error: (error) => {
        this.loaderService.hide();
        this.messageService.error(`Failed to get tiers`);
        var validationError = error?.error;
        if (validationError) {
          this.validationErrorService.show(validationError);
        }
      },
    });
  }

  ngOnInit() {
    this.subscribeToReportChange();
  }

  setDefaultState(): void {
    this.filterCache.clear();
    this.state = new GridState(this.pageSize, this.initialSort);

    this.fetchReportData();
  }

  subscribeToReportChange() {
    this.route.params.subscribe((params) => {
      this.reportType = params['type'];
      this.getTiers();
    });
  }

  fetchReportData() {
    this.loaderService.show();
    if (!this.state.sort?.length) {
      this.state.sort = this.initialSort;
    }

    this.state.group = this.group;
    if (this.selectedTier.id !== '-1') {
      this.state.filter = this.state.filter?.and({
        field: 'tierId',
        operator: 'eq',
        value: this.selectedTier?.id,
      });
    }

    this.reportService
      .getReportData(`modifiers/${this.reportType}`, this.state)
      .subscribe({
        next: (data) => {
          this.modifierExceptions = data;

          this.loaderService.hide();
        },
        error: (error) => {
          this.loaderService.hide();
          this.messageService.error(`Failed to export modifier exceptions`);
          var validationError = error?.error;
          if (validationError) {
            this.validationErrorService.show(validationError);
          }
        },
      });
  }

  onDataStateChanged(event: DataStateChangeEvent) {
    this.state = event as GridState;

    this.fetchReportData();
  }

  exportExceptions() {
    this.loaderService.show();
    this.reportService
      .exportExceptions(`modifiers/${this.reportType}`)
      .subscribe({
        next: (data: Blob) => {
          const blob = new Blob([data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `Modifier_Exceptions_${new Date().toDateString()}.xlsx`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
          this.loaderService.hide();
        },
        error: (error) => {
          this.loaderService.hide();
          this.messageService.error(`Failed to export modifier exceptions`);
          var validationError = error?.error;
          if (validationError) {
            this.validationErrorService.show(validationError);
          }
        },
      });
  }
}
